<script lang="ts">
	/**
	 * The links to an invoice's document, wherever an invoice is listed (Design §4.6).
	 *
	 * `InvoiceList` is deliberately not a component — a collection of entities that the library
	 * already covers with `Panel` + `Table` — but **this** part is not layout. It is three rules
	 * that are wrong in ways nobody sees:
	 *
	 * - **Ours or theirs, never both.** One charge with two documents carrying two numbers is worse
	 *   than either alone: the customer files whichever they clicked, and the store's books do not
	 *   know about the other. The server withholds the provider's URLs once it has issued its own,
	 *   and this renders whichever arrived.
	 * - **`documentNumber` is tested as a non-empty string, never `!== null`.** The field is
	 *   nullable, so `!== null` looks right — and it is `true` for `undefined`, which is what a
	 *   payload missing the field reads as. The row would then offer a download that 400s while
	 *   hiding the provider link that works. This has already produced a receipt claiming invoice
	 *   number `undefined`.
	 * - **Ours is a fetch, not a navigation**, because the endpoint needs the bearer token. The
	 *   object URL is deliberately not revoked straight away: Safari cancels a navigation to a blob
	 *   that has already been released, and the browser reclaims it when the tab closes.
	 *
	 * Three support screens list invoices — the register, a customer and a sale — and a second copy
	 * of the three rules above is a second copy that can drift from this one. It always reads
	 * through the **admin** endpoints: the customer's twin, which resolves ownership through the
	 * caller, moved to AbstractID with the account pages, and the `admin` prop that chose between
	 * the two went with it.
	 *
	 * **There is a third answer, and it arrived with the legacy migration** (§13). An imported
	 * invoice carries no `documentNumber` — this store did not issue it — and no provider URL
	 * either, because the legacy store rendered the PDF itself and kept the bytes. Under the binary
	 * above every one of those rows fell to "Not available yet", whose own wording promises a
	 * document that *is coming*: false for an invoice from a store that no longer exists, on every
	 * screen, for every migrated customer. `legacyDocumentURL` is that third branch.
	 *
	 * **The credit notes for the invoice sit here too** (Design §4.6). A customer reading a row that
	 * says "Refunded" is looking for the paperwork *for that payment*, and leaving it only in a
	 * separate list below makes them match reference numbers by eye to find it. It is an addition
	 * rather than a replacement: that list still has to exist, because a one-time purchase the
	 * provider raised no invoice for is credited against its own charge and has no invoice row to
	 * hang from.
	 */

	import ExternalLinkIcon from './ExternalLinkIcon.svelte';
	import CreditNoteLink from './CreditNoteLink.svelte';
	import { fetchAdminInvoiceDocument, fetchAdminLegacyDocument } from '../AppNetwork';
	import { hasIssuedInvoiceDocument } from '../Utilities';
	import { toastError } from '../stores/toast';
	import type { InvoiceCreditNote } from '../AppTypes';

	/**
	 * Structural rather than one DTO, because the register, the customer screen and the sale screen
	 * each carry a different invoice shape. These fields are the whole of what the three rules above
	 * need, and naming them here is what stops a copy of the component existing per screen.
	 */
	export let invoice: {
		uuid: string;
		documentNumber: string | null;
		hostedInvoiceURL: string | null;
		invoicePDFURL: string | null;
		/** Whether the legacy store's own PDF was carried across for this invoice (§13). */
		hasLegacyDocument?: boolean;
		creditNotes?: InvoiceCreditNote[];
	};

	/**
	 * Whether the Store issued its own document for this invoice. See the note above.
	 *
	 * Shared with `invoiceReference`, which decides *which number* to print from the same test —
	 * the number on screen and the document behind the link have to agree, and two copies of one
	 * predicate is how they stop agreeing.
	 */
	$: hasOwnDocument = hasIssuedInvoiceDocument(invoice);

	/** Per row, so one invoice's spinner does not blank its neighbours. */
	let downloading: boolean = false;

	/**
	 * Whether the legacy store's own document is what this row has.
	 *
	 * Only where **we** issued nothing: an invoice this store numbered has its own PDF, and offering
	 * both would be the two-documents-one-charge problem the ours-or-theirs rule exists to prevent.
	 */
	$: hasLegacyDocument = !hasOwnDocument && invoice.hasLegacyDocument === true;

	async function fnOpenDocument(): Promise<void> {
		downloading = true;
		try {
			const blob = hasLegacyDocument
				? await fetchAdminLegacyDocument(invoice.uuid)
				: await fetchAdminInvoiceDocument(invoice.uuid);
			window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
		} catch (error) {
			toastError('The invoice could not be opened.', (error as Error).message);
		} finally {
			downloading = false;
		}
	}
</script>

<div class="flex flex-wrap gap-3">
	{#if hasOwnDocument}
		<!-- Titled with the number, so a customer looking for one invoice among many can see
		     which is which without opening them. -->
		<button
			type="button"
			class="text-primary-500 hover:underline disabled:opacity-50"
			title={`Invoice ${invoice.documentNumber}`}
			disabled={downloading}
			on:click={() => void fnOpenDocument()}
		>
			{downloading ? 'Opening…' : 'Invoice PDF'}
		</button>
	{:else if hasLegacyDocument}
		<!--
			The document the legacy store actually sent this customer (§13). Named for what it is
			rather than "Invoice PDF": it carries the old store's reference and its old layout, and a
			customer comparing it against a reference this store printed should be able to tell at a
			glance which system produced which.
		-->
		<button
			type="button"
			class="text-primary-500 hover:underline disabled:opacity-50"
			title="The invoice as the previous store issued it"
			disabled={downloading}
			on:click={() => void fnOpenDocument()}
		>
			{downloading ? 'Opening…' : 'Original invoice PDF'}
		</button>
	{:else}
		{#if invoice.hostedInvoiceURL !== null}
			<a
				class="text-primary-500 hover:underline"
				href={invoice.hostedInvoiceURL}
				target="_blank"
				rel="noopener noreferrer"
			>
				View<ExternalLinkIcon class="ml-1 inline h-3 w-3 align-baseline" />
			</a>
		{/if}
		{#if invoice.invoicePDFURL !== null}
			<a
				class="text-primary-500 hover:underline"
				href={invoice.invoicePDFURL}
				target="_blank"
				rel="noopener noreferrer"
			>
				PDF<ExternalLinkIcon class="ml-1 inline h-3 w-3 align-baseline" />
			</a>
		{/if}
		{#if invoice.hostedInvoiceURL === null && invoice.invoicePDFURL === null}
			<!-- Not an error: a draft invoice has no document yet, and will. -->
			<span class="text-default text-xs">Not available yet</span>
		{/if}
	{/if}

	<!--
		Outside the ours-or-theirs branch, and deliberately: a credit note is **ours whichever way
		the invoice went**. The store issues one for a refund against an invoice the provider
		numbered just as readily as against its own, so nesting this inside the `{:else}` would hide
		the document on exactly the invoices whose own PDF the customer cannot get from us either.

		One per note, because several partial refunds each produce one, and each is named by its own
		number — two credit notes against one invoice are otherwise indistinguishable on the row.
	-->
	{#each invoice.creditNotes ?? [] as creditNote (creditNote.uuid)}
		<CreditNoteLink
			uuid={creditNote.uuid}
			documentNumber={creditNote.documentNumber}
			label="Credit note"
		/>
	{/each}
</div>
